/**
 * Missions: small anonymous cards from the game. Some come from the AI, some were planted by a
 * Signal — the payload gives no way to tell, on purpose. Completing pays points; skipping is
 * silent and reported to nobody.
 *
 * Three things happen ON the card rather than beside it, and they are all the same argument:
 *
 *   The reward is STAMPED across the card that earned it — a band held for a beat — rather than
 *   announced in a separate panel underneath. A toast is a receipt for an event; a stamp is an
 *   answer about the thing you just pressed, and the thing you just pressed is what you are
 *   looking at.
 *
 *   The reward is also PRICED BEFORE THE TAP. While you are behind on the week, missions pay
 *   double — and the chip used to say "+3" on a card whose stamp then said "+6", which is a
 *   catch-up rule nobody can act on because nobody can see it. The chip says "+3 → +6" and the
 *   card says why.
 *
 *   The skip is GRADED, stays silent, and CAN BE TAKEN BACK. "Not for me" opens three quiet
 *   reasons in place of the action row, so one skip teaches the surfacing engine three times as
 *   much as a bare no. None of it is reported to anybody, which is why the strip is worded as a
 *   preference and never as a message — the partner does not learn that a mission was offered, let
 *   alone declined. And because one of the three ("Never this kind") thins the pool for good, the
 *   band it lands on carries an Undo for as long as it is up.
 */
import { CoopCard, type Coop } from '@/components/coop-card';
import { Chip } from '@/components/chip';
import { Panel } from '@/components/panel';
import { Empty } from '@/components/empty';
import { Points } from '@/components/points';
import { Stamped, type Stamp } from '@/components/stamp-band';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHead } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { api } from '@/api';
import { cn } from '@/lib/utils';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { usePoll } from '@/usePoll';
import { useStore } from '@/store';
import * as React from 'react';
import { View } from 'react-native';

interface Mission {
  id: string;
  title: string;
  detail: string | null;
  status: 'surfaced' | 'seen' | 'done' | 'skipped';
  points: number;
  surfacedAt: string | null;
  doneAt: string | null;
  skippedAt: string | null;
}

/** The catch-up rule as the server states it: whether it is on, and what it multiplies by. */
interface CatchUp {
  active: boolean;
  multiplier: number;
}

/**
 * The three grades of no, in the order they get less recoverable: a timing objection, a taste
 * objection, a standing one. The wording stays flat and unapologetic — a reason strip that reads
 * as a confession is one people press past rather than answer.
 *
 * The third carries its own reassurance in its label, because it is the only one of the three that
 * is permanent: it thins the gesture pool for good, and a person deciding whether to press it is
 * entitled to know that it is not a door locking behind them. It is also the reason the band it
 * lands on has an Undo at all.
 */
type SkipReason = 'not_now' | 'not_my_thing' | 'never_kind';
const SKIP_REASONS: { reason: SkipReason; label: string }[] = [
  { reason: 'not_now', label: 'Not now' },
  { reason: 'not_my_thing', label: 'Not my thing' },
  { reason: 'never_kind', label: 'Never this kind · you can change this later' },
];

/**
 * How long a skip's band holds, which is how long the Undo is reachable.
 *
 * Longer than the app's shared 700ms beat, and for a different job: 700ms is measured to be long
 * enough to READ a stamp, and this one has to be long enough to notice a mistake, decide about it
 * and reach the word. The skip itself is already written server-side by the time the band appears,
 * so nothing is being held back during it — this is a window, not a pending write.
 */
const UNDO_HOLD_MS = 3200;

/**
 * The day a done mission was banked, as the board writes it: "Tue · +3".
 *
 * A fixed table rather than `toLocaleDateString(…, { weekday: 'short' })`, because Intl's options
 * are the first thing a Hermes build without full ICU drops — and it drops them by returning the
 * WHOLE date, so the failure reads as "9/2/2026 · +3" rather than as nothing. The app is English
 * throughout and the board writes English day names, so there is nothing here for a locale to say.
 */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * WHEN A CLOSED MISSION CLOSED — the one date a history can be ordered and grouped by.
 *
 * A done row has `doneAt` and a skipped one has `skippedAt`; the fallback to `surfacedAt` covers
 * rows written before that column existed rather than dropping them out of the list. The server
 * orders on exactly this expression, so the grouping here and the ordering there cannot disagree.
 */
function closedAt(m: Mission): Date | null {
  const iso = m.doneAt ?? m.skippedAt ?? m.surfacedAt;
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * A closed row's day, or null when the row cannot supply one.
 *
 * An unparseable timestamp drops the day and keeps the points, rather than printing "Invalid Date"
 * beside a real number: the points are the fact and the day is the garnish.
 */
function weekdayOf(m: Mission): string | null {
  const at = closedAt(m);
  return at ? WEEKDAYS[at.getDay()]! : null;
}

/**
 * Midnight on the Monday of `at`'s week, in the DEVICE's timezone.
 *
 * Local rather than the server's scoring week, and for the same reason the List's tally is: the
 * weeks in a history are the weeks the two of them lived in, one calendar on one wall, and a
 * boundary that fell at a UTC midnight would file Sunday evening under next week.
 */
function weekOf(at: Date): number {
  const d = new Date(at.getFullYear(), at.getMonth(), at.getDate());
  // getDay() is Sunday-based (0…6); `(day + 6) % 7` is the shift that makes Monday the first day.
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.getTime();
}

/** "This week", "Last week", or the week's own Monday. Relative for the two that have a name. */
function weekLabel(week: number, now: Date): string {
  const thisWeek = weekOf(now);
  if (week === thisWeek) return 'This week';
  if (week === thisWeek - 7 * 86_400_000) return 'Last week';
  const d = new Date(week);
  return `Week of ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** Which card is stamped, and with what. `id` is this screen's; the rest is the band's. */
type CardStamp = Stamp & { id: string };

/** How many closed rows the summary card shows before "All missions" is the way to see more. */
const RECENT_ROWS = 5;

export function MissionsScreen() {
  const { refreshBadges, refreshInvites, invitesFor, fail } = useStore();
  const [missions, setMissions] = React.useState<Mission[]>([]);
  const [coop, setCoop] = React.useState<Coop | null>(null);
  const [catchUp, setCatchUp] = React.useState<CatchUp>({ active: false, multiplier: 1 });
  /** Which card is showing its reason strip. One at a time — this is a decision, not a filter. */
  const [skipping, setSkipping] = React.useState<string | null>(null);
  /** The whole history, in place of the panel's usual body. A view, not a route — see below. */
  const [showAll, setShowAll] = React.useState(false);
  const [stamp, setStamp] = React.useState<CardStamp | null>(null);
  /** The stamp holds across an await, and this screen is one tab press from being unmounted. */
  const alive = React.useRef(true);
  React.useEffect(() => () => {
    alive.current = false;
  }, []);

  const load = React.useCallback(async () => {
    // The week's challenge belongs here as much as on the Scoreboard: tonight's question is a
    // mission, and asking it on a tab called Score is asking it in the wrong place. The same
    // payload carries the catch-up rule, which is what prices the cards below.
    const [rows, score] = await Promise.all([
      api.get<Mission[]>('/api/missions'),
      api.get<{ coop: Coop | null; catchUp: CatchUp }>('/api/score'),
    ]);
    setCoop(score.coop);
    setCatchUp(score.catchUp ?? { active: false, multiplier: 1 });
    setMissions(rows);
    if (rows.some((m) => m.status === 'surfaced')) {
      // Rendering the panel clears "surfaced-but-unseen" — the badge and the nudges follow.
      //
      // Every poll, not once per mount. This used to be a ref set on the first render with a
      // surfaced mission and never reset, so a mission that surfaced DURING the 20s poll kept its
      // badge until you left the tab and came back. No extra bookkeeping is needed to stop it
      // firing every tick: the POST flips every surfaced row to 'seen' server-side, so the next
      // poll's rows no longer match this test unless something genuinely new has surfaced.
      // Its own catch, and only its own: a bookkeeping POST that fails is not a reason to say the
      // panel would not load, when the missions it is about are already on the screen.
      await api
        .post('/api/missions/seen')
        .then(() => refreshBadges())
        .catch(() => {});
    }
  }, [refreshBadges]);

  const { loaded, error, reload } = usePoll(load, 20_000);

  const open = missions.filter((m) => m.status === 'surfaced' || m.status === 'seen');
  const closed = missions.filter((m) => m.status === 'done' || m.status === 'skipped');
  const recent = closed.slice(0, RECENT_ROWS);

  /** What a mission actually pays right now, which is what its chip and its stamp both say. */
  const doubled = catchUp.active && catchUp.multiplier > 1;
  const payout = (m: Mission) => (doubled ? m.points * catchUp.multiplier : m.points);

  /**
   * Take the skip back, and put the card back with it.
   *
   * Clearing the stamp is what stops the hold and returns the card to the list — `Stamped` handed
   * a null stamp is a plain View again — and it happens FIRST, because the undo has to feel like
   * the tap it answers rather than like a request. The server catches up a moment later, and if it
   * refuses, the reload that follows puts the row back where the server says it is.
   */
  const undoSkip = async (id: string) => {
    setStamp(null);
    try {
      await api.post(`/api/missions/${id}/unskip`);
    } catch (e) {
      fail(e);
    }
    await Promise.all([load(), refreshBadges()]).catch(() => undefined);
  };

  const undoAction = (id: string) => (
    <Button variant="ghost" size="sm" flush={false} onPress={() => void undoSkip(id)}>
      <Text>Undo</Text>
    </Button>
  );

  /**
   * Done and skip end the same way: stamp the card, and let `Stamped` hold the beat and take the
   * card out. The stamp is set only AFTER the POST resolves — a reward shown before the server has
   * agreed is a reward for something that may not have landed, and an Undo offered on a skip the
   * server never took is an Undo for nothing.
   */
  const act = async (m: Mission, what: 'done' | 'skip', reason?: SkipReason) => {
    try {
      if (what === 'done') {
        const res = await api.post<{ pointsEarned: number }>(`/api/missions/${m.id}/done`);
        setStamp({ id: m.id, kind: 'done', points: res.pointsEarned });
      } else {
        // A bare skip is still a skip: the reason is an extra, never a required field.
        await api.post(`/api/missions/${m.id}/skip`, reason ? { reason } : undefined);
        setStamp({ id: m.id, kind: 'skipped', action: undoAction(m.id) });
      }
      setSkipping(null);
    } catch (e) {
      setStamp(null);
      fail(e);
    }
  };

  /**
   * The card has finished leaving, so the list it is no longer in can be fetched.
   *
   * The stamp is cleared LAST and only if this screen is still mounted: it holds across an await
   * and this panel is one tab press from being unmounted.
   */
  const stampLeft = async () => {
    await Promise.all([load(), refreshBadges()]).catch(() => undefined);
    if (alive.current) setStamp(null);
  };

  const refresh = async () => {
    await Promise.all([load(), refreshBadges(), refreshInvites()]);
  };

  if (!loaded) return (
    <Panel id="missions">
      <PanelSkeleton rows={3} />
    </Panel>
  );
  if (error) return (
    <Panel id="missions">
      <LoadFailed onRetry={reload} reason={error} />
    </Panel>
  );

  /**
   * THE WHOLE HISTORY, IN THE SAME PANEL.
   *
   * A view rather than a route: nothing about a list of finished missions is worth a URL, a back
   * stack entry or a second entry in the module registry, and the panel's header — its title, its
   * guide — is exactly as correct here as it is over the cards. The way out is the one ghost at
   * the top, which is where the way in was.
   */
  if (showAll) {
    return (
      <Panel id="missions">
        <View className="flex-row flex-wrap items-center justify-between gap-2">
          <Text variant="label">All missions</Text>
          <Button variant="ghost" size="sm" flush={false} onPress={() => setShowAll(false)}>
            <Text>‹ Back</Text>
          </Button>
        </View>
        {closed.length ? <History rows={closed} /> : (
          <Empty
            mark="◎"
            title="Nothing finished yet"
            body="Missions land here once you have done them or passed them over."
          />
        )}
      </Panel>
    );
  }

  return (
    <Panel id="missions">
      {coop ? <CoopCard coop={coop} invites={invitesFor('score')} onChange={refresh} /> : null}

      {open.length ? (
        open.map((m) => (
          /* `Stamped` owns the whole end of a mission: the band over the card, the hold, and the
             card collapsing out of the list before the refetch. Handed no stamp it is a plain
             View and costs nothing. A skip's band holds longer than a done one's, because it is
             the window its Undo lives in. */
          <Stamped
            key={m.id}
            stamp={stamp?.id === m.id ? stamp : null}
            holdMs={stamp?.id === m.id && stamp.kind === 'skipped' ? UNDO_HOLD_MS : undefined}
            onLeft={() => void stampLeft()}>
            <Card>
              <CardContent>
                {/* The head band IS the title row — what the boards call `.mission-head`: the title
                    in band ink on the left, the points chip riding on the panel at the right.

                    `z-10` is what keeps it above the stamp. The band is the card's identity, so it
                    is lifted out of the veil rather than washed by it: a 72% card-coloured scrim
                    over the red panel would leave a white title at about 1.3:1, which is the one
                    thing a finished card must not become. (Web only — React Native orders `zIndex`
                    among siblings, and the stamp is the CardContent's sibling rather than this
                    one's. Native parity is a later stage.) */}
                <CardHead row className="relative z-10">
                  <Text variant="heading" className="min-w-0 flex-1">
                    {m.title}
                  </Text>
                  <Points value={m.points} to={doubled ? payout(m) : undefined} className="self-center" />
                </CardHead>

                {/* The rule behind the arrow, once per card and in the reward colour: an offer with
                    no reason attached reads as a rendering accident rather than as a rule. */}
                {doubled ? (
                  <Text variant="small" className="text-gold-text font-semibold">
                    Double while you’re behind
                  </Text>
                ) : null}

                {m.detail && m.detail !== m.title ? (
                  <Text className="text-muted-foreground">{m.detail}</Text>
                ) : null}

                {skipping === m.id ? (
                  /* The reason strip REPLACES the action row rather than sitting under it: a card
                     offering "Did it" beside three grades of no is asking two questions at once. */
                  <View className="gap-2">
                    {/* EQUAL WIDTHS, AND NEVER A NARROW LONE THIRD. Three chips sized to their own
                        labels put "Not now" beside "Not my thing" and orphaned the longest one on
                        a line of its own at a third of the width, which reads as a leftover rather
                        than as the third answer. A half-width basis makes them a two-column block
                        that the third spans when it wraps; the cap keeps them from becoming two
                        420px slabs on a desktop. */}
                    <View className="flex-row flex-wrap gap-2">
                      {SKIP_REASONS.map((r) => (
                        <Chip
                          key={r.reason}
                          label={r.label}
                          className="grow basis-[calc(50%-4px)] max-w-[260px]"
                          onPress={() => act(m, 'skip', r.reason)}
                        />
                      ))}
                    </View>
                    <View className="flex-row flex-wrap items-center gap-2">
                      <Text variant="small" className="text-muted-foreground min-w-0 flex-1">
                        Tunes what you are offered next.
                      </Text>
                      <Button variant="ghost" size="sm" flush={false} onPress={() => setSkipping(null)}>
                        <Text>Back</Text>
                      </Button>
                    </View>
                  </View>
                ) : (
                  /* One primary and one secondary, which is the boards' whole action vocabulary. The
                     heights differ on purpose: "Did it" is the thumb's target at 44, and the grade
                     of no beside it is the smaller 38. */
                  <View className="flex-row flex-wrap items-center gap-2">
                    <Button onPress={() => act(m, 'done')}>
                      <Text>Did it</Text>
                    </Button>
                    <Button variant="secondary" size="sm" onPress={() => setSkipping(m.id)}>
                      <Text>Not for me</Text>
                    </Button>
                  </View>
                )}
              </CardContent>
            </Card>
          </Stamped>
        ))
      ) : (
        <Empty
          mark="◎"
          title="No open missions"
          body="The game drops them when it drops them — never the moment anything happened, which is what keeps them anonymous."
        />
      )}

      <Text variant="small" className="text-muted-foreground">
        Some come from the game; some were quietly planted. You cannot tell which. Skipping is silent and shapes what
        you are offered next.
      </Text>

      {/* RECENT IS A CARD, and it is in recent order.
          It used to be a bare run of hairline rows under the panel's own furniture — the one list
          in the app drawn on the ground rather than on a sheet — and it was sorted by when each
          mission SURFACED while labelled with the day it was DONE, which is two clocks in one
          list. The server orders on the closing date now; this shows the last few and hands the
          rest to the history view. */}
      {recent.length ? (
        <Card>
          <CardContent className="gap-0">
            <View className="flex-row flex-wrap items-center justify-between gap-2 pb-1">
              <Text variant="label">Recent</Text>
              {closed.length > RECENT_ROWS ? (
                <Button variant="ghost" size="sm" flush={false} onPress={() => setShowAll(true)}>
                  <Text>All missions ›</Text>
                </Button>
              ) : null}
            </View>
            {recent.map((m, i) => (
              <ClosedRow key={m.id} mission={m} first={i === 0} />
            ))}
          </CardContent>
        </Card>
      ) : null}
    </Panel>
  );
}

/**
 * One finished mission: what it was, and what it came to.
 *
 * Hairline rows, not a stack of faded lines. What was done and what was passed over are told apart
 * by ink rather than by opacity — a done row is at full strength in the body colour with a gold
 * tick, a skipped one is muted throughout — and each carries its own meta on the right, which is
 * the half that was missing: "Tue · +3" says the mission paid and when, where a bare title says
 * only that something happened to it.
 */
function ClosedRow({ mission, first }: { mission: Mission; first?: boolean }) {
  const done = mission.status === 'done';
  const day = weekdayOf(mission);
  return (
    <View className={cn('flex-row items-center gap-3 py-3', !first && 'border-border border-t')}>
      <Text className={done ? 'text-gold-text font-semibold' : 'text-muted-foreground'}>{done ? '✓' : '–'}</Text>
      <Text className={cn('min-w-0 flex-1', !done && 'text-muted-foreground')}>{mission.title}</Text>
      {/* A done row's meta is the reward, so it takes gold-text as both sheets' `.recent .meta`
          do; a skip's is a fact and stays muted — and it carries its day too, now that a skip has
          one, so the two kinds of row are the same shape. */}
      <Text variant="small" className={done ? 'text-gold-text' : 'text-muted-foreground'}>
        {done
          ? day
            ? `${day} · +${mission.points}`
            : `+${mission.points}`
          : day
            ? `${day} · skipped`
            : 'skipped'}
      </Text>
    </View>
  );
}

/**
 * The history, in weeks.
 *
 * A flat run of fifty rows is a scroll with no landmarks in it; the weeks are what let somebody
 * answer "was that this week or the one before" without counting. The rows arrive already in
 * closing order from the server, so grouping only has to walk them and cut where the week changes
 * — nothing is re-sorted here, and a row whose date cannot be read keeps its place under the
 * heading its neighbours are in rather than being dropped.
 */
function History({ rows }: { rows: Mission[] }) {
  const now = new Date();
  const groups: { week: number | null; rows: Mission[] }[] = [];
  for (const m of rows) {
    const at = closedAt(m);
    const week = at ? weekOf(at) : null;
    const last = groups[groups.length - 1];
    if (last && last.week === week) last.rows.push(m);
    else groups.push({ week, rows: [m] });
  }

  return (
    <>
      {groups.map((g, i) => (
        <Card key={g.week ?? `undated-${i}`}>
          <CardContent className="gap-0">
            <Text variant="label" className="pb-1">
              {g.week === null ? 'Undated' : weekLabel(g.week, now)}
            </Text>
            {g.rows.map((m, j) => (
              <ClosedRow key={m.id} mission={m} first={j === 0} />
            ))}
          </CardContent>
        </Card>
      ))}
    </>
  );
}
